import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import HelpChatbot from '../components/HelpChatbot';
import { Card } from '@/components/ui/card';
import { Activity, Heart, Calendar, FileText, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { Link } from 'react-router-dom';
import VisitorStatsWidget from '../components/VisitorStatsWidget';

const Dashboard = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-teal-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      {/* Main content with proper spacing from fixed nav */}
      <div className="container mx-auto px-4 py-6 sm:py-8 mt-4" data-testid="dashboard-container">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            {t('dashboard.welcomeBack')}, {user.full_name}!
          </h1>
          <p className="text-gray-600 text-lg">{t('dashboard.healthOverview')}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-xl hover:shadow-2xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <Heart className="w-10 h-10" />
              <span className="text-3xl font-bold">{stats?.latest_health_data?.heart_rate || '--'}</span>
            </div>
            <p className="text-sm opacity-90">{t('healthMetrics.heartRate')} ({t('units.bpm')})</p>
            <p className="text-xs opacity-75 mt-1">{t('dashboard.latestReading')}</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-cyan-500 to-teal-600 text-white shadow-xl hover:shadow-2xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <Activity className="w-10 h-10" />
              <span className="text-3xl font-bold">{stats?.latest_health_data?.steps || '--'}</span>
            </div>
            <p className="text-sm opacity-90">{t('dashboard.stepsToday')}</p>
            <p className="text-xs opacity-75 mt-1">{t('dashboard.keepMoving')}</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-teal-600 to-cyan-700 text-white shadow-xl hover:shadow-2xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <Calendar className="w-10 h-10" />
              <span className="text-3xl font-bold">{stats?.upcoming_appointments || 0}</span>
            </div>
            <p className="text-sm opacity-90">{t('dashboard.upcomingAppointments')}</p>
            <p className="text-xs opacity-75 mt-1">{t('dashboard.nextConsultation')}</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-cyan-600 to-teal-700 text-white shadow-xl hover:shadow-2xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <FileText className="w-10 h-10" />
              <span className="text-3xl font-bold">{stats?.reports_count || 0}</span>
            </div>
            <p className="text-sm opacity-90">{t('nav.reports')}</p>
            <p className="text-xs opacity-75 mt-1">{t('dashboard.uploadedDocuments')}</p>
          </Card>
        </div>

        {/* {t('dashboard.quickStats')} */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Link to="/symptom-checker">
            <Card className="p-8 bg-white border-2 border-teal-100 hover:border-teal-300 hover:shadow-xl transition-all duration-300 cursor-pointer group">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Activity className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800">{t('dashboard.checkSymptoms')}</h3>
              <p className="text-gray-600">{t('dashboard.getAiAnalysis')}</p>
            </Card>
          </Link>

          <Link to="/appointments">
            <Card className="p-8 bg-white border-2 border-cyan-100 hover:border-cyan-300 hover:shadow-xl transition-all duration-300 cursor-pointer group">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-100 to-teal-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Calendar className="w-8 h-8 text-cyan-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800">{t('dashboard.bookAppointment')}</h3>
              <p className="text-gray-600">{t('dashboard.scheduleConsultation')}</p>
            </Card>
          </Link>

          <Link to="/health-data">
            <Card className="p-8 bg-white border-2 border-teal-100 hover:border-teal-300 hover:shadow-xl transition-all duration-300 cursor-pointer group">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800">{t('dashboard.syncHealthData')}</h3>
              <p className="text-gray-600">{t('dashboard.updateSmartwatch')}</p>
            </Card>
          </Link>
        </div>

        {/* Health Insights Banner */}
        <Card className="p-8 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">{t('dashboard.yourHealthJourney')}</h3>
              <p className="text-teal-50 text-lg">
                {stats?.medical_history_count || 0} {t('dashboard.medicalRecordsTracked')}
              </p>
            </div>
            <Heart className="w-24 h-24 opacity-20" />
          </div>
        </Card>

        {/* Analytics Widget for Doctors */}
        {user.role === 'doctor' && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('dashboard.siteAnalytics')}</h2>
            <VisitorStatsWidget />
          </div>
        )}
      </div>
      
      {/* Help Chatbot */}
      <HelpChatbot user={user} />
    </div>
  );
};

export default Dashboard;
